import { useEffect, useState } from 'react';
import {
  AlertTriangle,
  CheckCircle2,
  CircleStop,
  Download,
  Info,
  Loader2,
  Lock,
  ShieldX,
  XCircle,
} from 'lucide-react';
import clsx from 'clsx';
import type { QueryResult } from '@idas/shared';
import { ResultGrid } from './ResultGrid';

interface Props {
  result: QueryResult | null;
  running: boolean;
  error: string | null;
  canExport: boolean;
  onExport: () => void;
  /** Stops the running query. Also offered here, where the eye already is. */
  onCancel: () => void;
  canCancel: boolean;
}

type Tab = 'results' | 'messages';

export function ResultPanel({
  result,
  running,
  error,
  canExport,
  onExport,
  onCancel,
  canCancel,
}: Props) {
  const [tab, setTab] = useState<Tab>('results');

  const noticeCount =
    (result?.warnings.length ?? 0) + (result?.violations.length ?? 0) + (result?.error ? 1 : 0);

  return (
    <div className="flex h-full flex-col bg-surface">
      <div className="flex h-8 shrink-0 items-center gap-1 border-b border-edge bg-surface-raised px-2">
        <TabButton active={tab === 'results'} onClick={() => setTab('results')}>
          Results
          {result?.status === 'SUCCESS' && (
            <span className="ml-1 tabular-nums text-ink-faint">
              {result.rowCount.toLocaleString()}
            </span>
          )}
        </TabButton>

        <TabButton active={tab === 'messages'} onClick={() => setTab('messages')}>
          Messages
          {noticeCount > 0 && (
            <span
              className={clsx(
                'ml-1 rounded-full px-1.5 text-2xs tabular-nums',
                result?.violations.length || result?.error
                  ? 'bg-danger/15 text-danger'
                  : 'bg-warn/15 text-warn',
              )}
            >
              {noticeCount}
            </span>
          )}
        </TabButton>

        <div className="flex-1" />

        {result && <StatusSummary result={result} />}

        {result?.status === 'SUCCESS' && result.rowCount > 0 && canExport && (
          <button className="btn btn-ghost ml-2 px-1.5" onClick={onExport} title="Export results">
            <Download className="h-3.5 w-3.5" />
          </button>
        )}
      </div>

      <div className="min-h-0 flex-1">
        {running && <RunningState onCancel={onCancel} canCancel={canCancel} />}

        {!running && !result && !error && (
          <div className="flex h-full flex-col items-center justify-center gap-1 text-center">
            <p className="text-2xs text-ink-faint">
              Write a query and press{' '}
              <kbd className="rounded border border-edge bg-surface-sunken px-1 py-0.5 font-mono">
                Ctrl
              </kbd>
              {' + '}
              <kbd className="rounded border border-edge bg-surface-sunken px-1 py-0.5 font-mono">
                Enter
              </kbd>
            </p>
            <p className="text-2xs text-ink-faint">
              Select part of a query to run only that part.
            </p>
          </div>
        )}

        {!running && error && (
          <div className="p-4">
            <Notice kind="error" title="Request failed">
              {error}
            </Notice>
          </div>
        )}

        {!running && result && tab === 'results' && (
          <>
            {result.status === 'SUCCESS' && result.rowCount > 0 && <ResultGrid result={result} />}

            {result.status === 'SUCCESS' && result.rowCount === 0 && (
              <div className="flex h-full items-center justify-center">
                <p className="text-2xs text-ink-faint">
                  The query ran successfully and matched no rows.
                </p>
              </div>
            )}

            {result.status !== 'SUCCESS' && (
              <div className="space-y-2 overflow-y-auto p-4">
                <MessagesFor result={result} />
              </div>
            )}
          </>
        )}

        {!running && result && tab === 'messages' && (
          <div className="space-y-2 overflow-y-auto p-4">
            <MessagesFor result={result} />
            {noticeCount === 0 && result.status === 'SUCCESS' && (
              <Notice kind="ok" title="No issues">
                The query ran cleanly with nothing worth flagging.
              </Notice>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function MessagesFor({ result }: { result: QueryResult }) {
  return (
    <>
      {result.violations.map((violation, index) => (
        <Notice key={index} kind="denied" title={violation.code.replace(/_/g, ' ').toLowerCase()}>
          <p>{violation.message}</p>
          {violation.ruleReason && (
            <p className="mt-1.5 border-l-2 border-lock/40 pl-2 italic text-ink-muted">
              {violation.ruleReason}
            </p>
          )}
        </Notice>
      ))}

      {result.error && (
        <Notice kind="error" title={result.error.code ?? 'Database error'}>
          {result.error.message}
        </Notice>
      )}

      {result.warnings.map((warning, index) => (
        <Notice key={`w${index}`} kind="warn" title="Note">
          {warning}
        </Notice>
      ))}
    </>
  );
}

function StatusSummary({ result }: { result: QueryResult }) {
  if (result.status === 'DENIED') {
    return (
      <span className="chip border-lock/30 bg-lock/10 text-lock">
        <ShieldX className="h-3 w-3" />
        blocked by policy
      </span>
    );
  }
  if (result.status === 'SUCCESS') {
    return (
      <div className="flex items-center gap-2 text-2xs text-ink-faint">
        {result.columns.some((c) => c.masked) && (
          <span className="chip border-lock/30 bg-lock/10 text-lock">
            <Lock className="h-2.5 w-2.5" />
            masked
          </span>
        )}
        {result.truncated && (
          <span className="chip border-warn/30 bg-warn/10 text-warn">truncated</span>
        )}
        <span className="tabular-nums">{formatDuration(result.durationMs)}</span>
      </div>
    );
  }
  return (
    <span className="chip border-danger/30 bg-danger/10 text-danger">
      {result.status.toLowerCase()}
    </span>
  );
}

/** Column widths that read as real data rather than a repeating pattern. */
const SKELETON_WIDTHS = [
  ['62%', '38%', '84%', '46%', '28%'],
  ['48%', '72%', '55%', '90%', '41%'],
  ['78%', '44%', '36%', '63%', '52%'],
  ['54%', '86%', '70%', '32%', '68%'],
  ['66%', '30%', '58%', '76%', '38%'],
  ['42%', '64%', '92%', '50%', '60%'],
  ['70%', '52%', '40%', '82%', '34%'],
  ['58%', '78%', '48%', '36%', '74%'],
];

/**
 * The wait between pressing Run and seeing rows.
 *
 * A spinner says only "something is happening". A skeleton of the grid says
 * what is coming and gives the eye somewhere to settle — and against a
 * database several seconds away, the elapsed counter is the difference
 * between "working" and "stuck", which is the actual question being asked.
 */
function RunningState({ onCancel, canCancel }: { onCancel: () => void; canCancel: boolean }) {
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    const started = Date.now();
    const timer = setInterval(() => setElapsed(Date.now() - started), 100);
    return () => clearInterval(timer);
  }, []);

  const seconds = elapsed / 1000;

  return (
    <div className="relative h-full overflow-hidden">
      <div aria-hidden="true" className="select-none">
        {/* Header band, matching the real grid's height and rhythm. */}
        <div className="flex h-7 items-center gap-4 border-b border-edge-strong bg-surface-raised px-3">
          {SKELETON_WIDTHS[0]!.map((_, index) => (
            <div
              key={index}
              className="h-2 rounded-sm bg-edge-strong/50"
              style={{ width: `${54 + ((index * 17) % 46)}px` }}
            />
          ))}
        </div>

        {SKELETON_WIDTHS.map((row, rowIndex) => (
          <div
            key={rowIndex}
            className={clsx(
              'flex h-[26px] items-center gap-4 border-b border-edge/50 px-3',
              rowIndex % 2 === 1 && 'bg-surface-raised/40',
            )}
            // Each row fades slightly further out, so the block reads as a
            // list continuing past the fold rather than a fixed panel.
            style={{ opacity: 1 - rowIndex * 0.1 }}
          >
            {row.map((width, cellIndex) => (
              <div
                key={cellIndex}
                className="h-2 rounded-sm bg-edge/70"
                style={{ width: `calc(${width} * 0.22)`, minWidth: 28 }}
              />
            ))}
          </div>
        ))}
      </div>

      {/* A single sweep across the whole block, rather than per-element
          pulsing — one moving highlight reads as loading; eight competing
          ones read as noise. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 animate-sweep"
        style={{
          background:
            'linear-gradient(100deg, transparent 20%, rgb(var(--accent) / 0.07) 50%, transparent 80%)',
        }}
      />

      <div className="pointer-events-none absolute inset-x-0 bottom-0 flex items-center justify-center gap-3 bg-gradient-to-t from-surface via-surface/90 to-transparent pb-5 pt-10">
        <span className="flex items-center gap-2 text-2xs text-ink-muted">
          <Loader2 className="h-3 w-3 animate-spin text-accent" />
          Running
          <span className="tabular-nums text-ink-faint">{seconds.toFixed(1)}s</span>
        </span>
        {canCancel && (
          <button className="btn pointer-events-auto" onClick={onCancel}>
            <CircleStop className="h-3 w-3" />
            Cancel
          </button>
        )}
      </div>

      {/* Reassurance only once the wait is long enough to feel wrong. */}
      {seconds > 12 && (
        <p className="pointer-events-none absolute inset-x-0 bottom-1 text-center text-2xs text-ink-faint">
          Still going — large scans can take a while.
        </p>
      )}
    </div>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      className={clsx(
        'flex items-center rounded px-2 py-1 text-2xs font-medium transition-colors',
        active ? 'bg-surface-sunken text-ink' : 'text-ink-muted hover:text-ink',
      )}
      onClick={onClick}
    >
      {children}
    </button>
  );
}

const NOTICE_STYLES = {
  ok: { border: 'border-ok/30 bg-ok/5', text: 'text-ok', Icon: CheckCircle2 },
  warn: { border: 'border-warn/30 bg-warn/5', text: 'text-warn', Icon: AlertTriangle },
  error: { border: 'border-danger/30 bg-danger/5', text: 'text-danger', Icon: XCircle },
  denied: { border: 'border-lock/30 bg-lock/5', text: 'text-lock', Icon: ShieldX },
  info: { border: 'border-edge bg-surface-raised', text: 'text-ink-muted', Icon: Info },
} as const;

function Notice({
  kind,
  title,
  children,
}: {
  kind: keyof typeof NOTICE_STYLES;
  title: string;
  children: React.ReactNode;
}) {
  const { border, text, Icon } = NOTICE_STYLES[kind];
  return (
    <div className={clsx('rounded-md border p-3', border)}>
      <div className={clsx('mb-1 flex items-center gap-1.5 text-2xs font-semibold', text)}>
        <Icon className="h-3.5 w-3.5" />
        {title}
      </div>
      <div className="text-2xs leading-relaxed text-ink">{children}</div>
    </div>
  );
}

function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms} ms`;
  return `${(ms / 1000).toFixed(2)} s`;
}
